import React, { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import isEqual from 'lodash/isEqual'; // Import lodash for deep comparison
import { Box, Flex, Inline, Text, xcss } from '@atlaskit/primitives';
import { Card } from '@atlaskit/smart-card';
import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button/new';
import Pressable from '@atlaskit/primitives/pressable';
import Lozenge from '@atlaskit/lozenge';
import PremiumIcon from '@atlaskit/icon/core/premium';
import { SmartCardProviderMigration } from '@atlassian/jira-linking-platform-provider-migration/src/index.tsx';
import AIContainer from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-container/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useResizeObserver } from '@atlassian/jira-react-use-resize-observer/src/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { StreamingStatusBar } from '../streaming-status-bar/index.tsx';
import { steps, useRelatedIssues, type ResourceSuggestion } from '../../controllers/context.tsx';
import {
	type RelatedIssuesSearchPayload,
	useFetchRelatedIssue,
} from '../../services/use-fetch-related-issues/index.tsx';

import messages from './messages.tsx';

type SuggestRelatedIssuesModalProps = {
	children: React.ReactNode;
	existingIssues: string[];
	customRelatedIssueSearchPayload?: RelatedIssuesSearchPayload;
};

interface ToggleButtonContentProps {
	shouldDisplay: boolean;
	draftSuggestions: ResourceSuggestion[] | undefined;
	errorMessage: unknown | null;
	formatMessage: (message: { defaultMessage: string }) => string;
}

/**
 * Content component for the toggle button that shows different states:
 * 1. No items found
 * 2. Error message
 * 3. View items with count
 * 4. Hide suggestions
 */
const ToggleButtonContent: FC<ToggleButtonContentProps> = ({
	shouldDisplay,
	draftSuggestions,
	errorMessage,
	formatMessage,
}) => {
	if (!shouldDisplay) {
		if (errorMessage !== null) {
			return (
				<Text weight="medium" color="color.text.subtle">
					{formatMessage(
						fg('aix-967-similar-issues-content-review')
							? messages.updateDescriptionAndRetry
							: messages.somethingWentWrong,
					)}
				</Text>
			);
		}
		if (!draftSuggestions || draftSuggestions.length === 0) {
			return (
				<Text weight="medium" color="color.text.subtle">
					{formatMessage(
						fg('aix-967-similar-issues-content-review')
							? messages.noSimilarWorkItems
							: messages.noItemsFound,
					)}
				</Text>
			);
		}
		return (
			<Flex alignItems="center" gap="space.100">
				<Text weight="medium" color="color.text.subtle">
					{formatMessage(messages.viewItems)}
				</Text>{' '}
				<Badge>{draftSuggestions.length}</Badge>
			</Flex>
		);
	}
	return (
		<Text weight="medium" color="color.text.subtle">
			{formatMessage(messages.hideSuggestions)}
		</Text>
	);
};

const UNCATEGORISED_ERROR = 'UNKNOWN ERROR';

export const ProactiveSimilarIssuesCollapsible: FC<SuggestRelatedIssuesModalProps> = ({
	children,
	existingIssues,
	customRelatedIssueSearchPayload,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [
		{ currentStep, resourceSuggestions, errorMessage, invokedFrom },
		{
			setRelatedIssuesDisplayStep,
			setRelatedIssuesLoadedStep,
			discardAllSuggestions,
			setResourceSuggestion,
			setErrorMessage,
			setRelatedIssuesFetchStep,
		},
	] = useRelatedIssues();
	const { data, error, fetchSuggestion } = useFetchRelatedIssue(existingIssues);

	const isFetching = useMemo(() => currentStep === steps.fetching, [currentStep]);
	const shouldDisplay = useMemo(() => currentStep === steps.display, [currentStep]);
	const isLoaded = useMemo(() => currentStep === steps.loaded, [currentStep]);

	const [numOfSmartLinksToShow, setNumOfSmartLinksToShow] = useState(0);
	/**
	 * We use useResizeObserver to determine the width of the container and adjust the display of smart links
	 */
	const aiContainerRef = useRef<HTMLDivElement>(null);

	const draftSuggestions = resourceSuggestions?.filter(
		(suggestion) => suggestion.resourceStatus === 'DRAFT',
	);

	const isDisabled = isFetching || !draftSuggestions?.length || errorMessage !== null;

	const onDismissSuggestions = useCallback(() => {
		discardAllSuggestions();
	}, [discardAllSuggestions]);

	const hasMounted = useRef(false);
	const hasFiredInitiatedAnalytics = useRef(false);
	const previousInvokedFromRef = useRef(invokedFrom);
	const dismissRef = useRef(onDismissSuggestions);
	const prevPayloadRef = useRef<RelatedIssuesSearchPayload | undefined>();

	// Keep the ref up to date with the latest callback
	useEffect(() => {
		dismissRef.current = onDismissSuggestions;
	}, [onDismissSuggestions]);

	// Effect for cleanup only - using the ref allows us to avoid dependencies
	useEffect(() => {
		return () => {
			dismissRef.current();
			hasMounted.current = false;
			hasFiredInitiatedAnalytics.current = false;
		};
	}, []);

	// Effect for initialization only
	useEffect(() => {
		if (
			!hasMounted.current ||
			(!isEqual(prevPayloadRef.current, customRelatedIssueSearchPayload) &&
				expVal('similar-work-items-in-gic', 'isEnabled', false))
		) {
			hasMounted.current = true;
			fetchSuggestion(customRelatedIssueSearchPayload);
			setRelatedIssuesFetchStep();
			if (expVal('similar-work-items-in-gic', 'isEnabled', false)) {
				prevPayloadRef.current = customRelatedIssueSearchPayload;
			}
		}
	}, [fetchSuggestion, setRelatedIssuesFetchStep, customRelatedIssueSearchPayload]);

	// Effect for tracking analytics when invokedFrom becomes available
	useEffect(() => {
		if (!hasFiredInitiatedAnalytics.current && invokedFrom !== null) {
			hasFiredInitiatedAnalytics.current = true;
			fireTrackAnalytics(createAnalyticsEvent({}), 'aiInteraction initiated', {
				invokedFrom,
				existingItemsCount: existingIssues.length,
			});
		}
	}, [invokedFrom, existingIssues.length, createAnalyticsEvent]);

	// Reset hasFiredInitiatedAnalytics when invokedFrom changes since its a new interaction initiated event
	useEffect(() => {
		if (previousInvokedFromRef.current !== invokedFrom) {
			hasFiredInitiatedAnalytics.current = false;
			previousInvokedFromRef.current = invokedFrom;
		}
	}, [invokedFrom]);

	useEffect(() => {
		if (data) {
			setResourceSuggestion(data);
			setRelatedIssuesLoadedStep();
			fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult viewed', {
				// This event does not meet the MAU criteria since users are being served a proactive suggestion
				doesNotMeetMAUCriteria: true,
				aiSuggestedCount: data?.length,
				smartCardClicked: 0,
				suggestionsExpanded: 0,
			});
		} else if (error) {
			setErrorMessage(error);
			setRelatedIssuesLoadedStep();
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiResult error',
				'aiRelatedIssuesSuggestionResultBox',
				{
					aiErrorCode: error instanceof FetchError ? error.statusCode : UNCATEGORISED_ERROR,
				},
			);
		}
	}, [
		setResourceSuggestion,
		setErrorMessage,
		data,
		error,
		createAnalyticsEvent,
		setRelatedIssuesLoadedStep,
	]);

	/**
	 * Event handler for the toggle button click
	 * Since we use the same button for both the Pressable and the Button click, we need to stop the event propagation to not fire the event twice
	 */
	const onToggleClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			if (currentStep === steps.loaded) {
				setRelatedIssuesDisplayStep();
				fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult viewed', {
					// This event specfically meets the MAU criteria since users are proactively viewing suggestions
					doesNotMeetMAUCriteria: false,
					aiSuggestedCount: data?.length,
					smartCardClicked: 0,
					suggestionsExpanded: 1,
				});
			} else if (currentStep === steps.display) {
				setRelatedIssuesLoadedStep();
				fireTrackAnalytics(
					createAnalyticsEvent({}),
					'aiResult actioned',
					'aiRelatedIssuesSuggestionResultBox',
					{
						aiResultAction: 'suggestionsHidden',
						aiItemsCount: data?.length,
					},
				);
			}
			e.stopPropagation();
		},
		[
			setRelatedIssuesDisplayStep,
			setRelatedIssuesLoadedStep,
			createAnalyticsEvent,
			currentStep,
			data,
		],
	);

	/**
	 * Static width calculated based on the manual checking of width of the lozenge and the width of Similar Items text + Icon
	 * Card width is 192px since it aligns with 12 rem in styles below
	 */
	const onSmartLinksContainerResize = useCallback(() => {
		if (aiContainerRef.current) {
			const cardWidth = 192;
			const staticWidth = 330;
			const { offsetWidth } = aiContainerRef.current;
			const availableWidth = offsetWidth - staticWidth;
			const numberOfCards =
				availableWidth >= cardWidth ? Math.floor(availableWidth / cardWidth) : 0;
			setNumOfSmartLinksToShow(numberOfCards);
		}
	}, []);

	const onSmartCardClick = useCallback(
		(issueLink: string) => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.open(issueLink, '_blank');
			fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult viewed', {
				doesNotMeetMAUCriteria: false,
				aiSuggestedCount: data?.length,
				smartCardClicked: 1,
				suggestionsExpanded: 0,
			});
		},
		[data, createAnalyticsEvent],
	);

	useEffect(() => {
		onSmartLinksContainerResize();
	}, [onSmartLinksContainerResize]);

	useResizeObserver({
		ref: aiContainerRef,
		onResize: onSmartLinksContainerResize,
	});

	return (
		<SmartCardProviderMigration>
			<AIContainer
				hasNewBorderExperience
				shouldFitContainer
				isShadow={false}
				isLoading={isFetching}
				spacing="none"
				testId="ai-related-issues.ui.proactive-collapsible-parent.ai-container"
				ref={aiContainerRef}
			>
				<Pressable
					xcss={[pressableStyles, !isDisabled && pressableEnabledStyles]}
					backgroundColor="color.background.neutral.subtle"
					onClick={(e) => onToggleClick(e)}
					isDisabled={isDisabled}
					testId="ai-related-issues.ui.proactive-collapsible-parent.button"
				>
					<Inline
						space="space.050"
						testId="ai-related-issues.ui.proactive-collapsible-parent.inline"
						xcss={headerActionsContainerStyles}
					>
						<Box as="span" xcss={aiIconLabelStyles}>
							<PremiumIcon
								label={formatMessage(
									fg('aix-967-similar-issues-content-review')
										? messages.similarWorkItems
										: messages.similarItems,
								)}
							/>
							<Text maxLines={1} color="color.text.subtle">
								{formatMessage(
									fg('aix-967-similar-issues-content-review')
										? messages.similarWorkItems
										: messages.similarItems,
								)}
							</Text>
							<Lozenge appearance="new">{formatMessage(messages.beta)}</Lozenge>
						</Box>
						{isFetching && (
							<StreamingStatusBar
								message={formatMessage(messages.findingResults)}
								hasPadding={false}
							/>
						)}
						{isLoaded &&
							(invokedFrom === 'globalIssueCreate' &&
							expVal('similar-work-items-in-gic', 'isEnabled', false) ? null : (
								<Box xcss={relatedItemsContainerStyles}>
									{draftSuggestions?.map((suggestion, index) => {
										// Dynamically show the number of smart links based on the width of the container
										if (index > numOfSmartLinksToShow - 1) return;
										return (
											<Box xcss={relatedItemCardStyles} key={suggestion.resource.id}>
												<Card
													appearance="inline"
													url={suggestion.resource.issueLink}
													truncateInline
													resolvingPlaceholder=""
													placeholder=" "
													showHoverPreview
													onClick={() => onSmartCardClick(suggestion.resource.issueLink)}
												/>
											</Box>
										);
									})}
								</Box>
							))}
					</Inline>
					{(isLoaded || shouldDisplay) && (
						<Inline xcss={toggleButtonWrapperStyles}>
							<Button
								spacing="compact"
								onClick={(e) => onToggleClick(e)}
								isDisabled={isDisabled}
								appearance="subtle"
							>
								<ToggleButtonContent
									shouldDisplay={shouldDisplay}
									draftSuggestions={draftSuggestions}
									errorMessage={errorMessage}
									formatMessage={formatMessage}
								/>
							</Button>
						</Inline>
					)}
				</Pressable>
				{shouldDisplay && children}
			</AIContainer>
		</SmartCardProviderMigration>
	);
};

const headerActionsContainerStyles = xcss({
	background: 'none',
	alignItems: 'center',
	marginLeft: 'space.100',
});

const aiIconLabelStyles = xcss({
	padding: 'space.025',
	paddingTop: '0',
	paddingBottom: '0',
	height: 'space.0',
	width: 'space.100',
	color: 'color.text.subtle',
	marginRight: 'space.100',
	gap: 'space.100',
	display: 'flex',
	minWidth: 'fit-content',
	alignItems: 'center',
});

const relatedItemsContainerStyles = xcss({
	gap: 'space.150',
	maxHeight: '100%',
	marginTop: 'space.025',
	display: 'flex',
	flexWrap: 'nowrap',
});

const relatedItemCardStyles = xcss({
	// This is 12 rem which is 192px - used in our calculation onSmartLinksContainerResize
	maxWidth: 'size.1000',
	textAlign: 'left',
});

const toggleButtonWrapperStyles = xcss({
	minWidth: 'fit-content',
	paddingRight: 'space.150',
	flexShrink: 0,
});

const pressableStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	height: 'size.400',
	borderRadius: '3px',
	width: '100%',
	paddingBlock: 'space.100',
	transition: 'background 0.1s ease-out',
});

const pressableEnabledStyles = xcss({
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});
